// Chakra imports
import {
  Box,
  Table,
  Tbody, Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
// Custom components
import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios'
import {useAuthHeader} from  'react-auth-kit'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useInfiniteQuery } from '@tanstack/react-query'
import "./modal.css"
import {SearchTransaction} from "../../Navbars/SearchBar/SearchTransaction";
import TablesTransactionRow from "../../TablesTransactionRow";
import { useLocation } from 'react-router-dom';

const fetchSize = 50

async function fetchTransactionsSimple(token, pageParam, fetchSize, terminalId)
{
  const currentTerminal = `current_terminal=${terminalId}&`
  const res = await axios.get(`/api/v1/transactions?${terminalId ? currentTerminal : ''}limit=${fetchSize}&page=${pageParam+1}`, {
    headers: {
      Authorization: token
    }
  });
  return await res.data?.data;
}

async function fetchTransactionsBySearch(token, search)
{
  const res = await axios.get(`/api/v1/transactions?unspecified_id=${search}`, {
    headers: {
      Authorization: token
    }
  });
  return await res.data?.data;
}

async function fetchTransactionsByTokenCard(token, tokenCard, pageParam, fetchSize)
{
  const res = await axios.get(`/api/v1/transactions/find_txs_by_card_token/${tokenCard}?limit=${fetchSize}&page=${pageParam+1}`, {
    headers: {
      Authorization: token
    }
  });
  return await res.data?.data;
}

async function fetchTransactions(token, criteria)
{
  // console.log('criteria', criteria);
  if (criteria.search) {
    return fetchTransactionsBySearch(token, criteria.search);
  } else if (criteria.tokenCard) {
    return fetchTransactionsByTokenCard(token, criteria.tokenCard, criteria.pageParam, criteria.fetchSize);
  } else {
    return fetchTransactionsSimple(token, criteria.pageParam, criteria.fetchSize, criteria?.terminalId);
  }
}

const TransactionsTable = ({ title, columns }) => {
  const { search } = useLocation();

  const query = new URLSearchParams(search);

  const textColor = useColorModeValue("gray.700", "white");
  const tableContainerRef = useRef(null)
  const token = useAuthHeader();

  const [criteria, setCriteria] = useState({
    search: '',
    tokenCard: ''
  });


  const { data, fetchNextPage, isFetching, isLoading } =
    useInfiniteQuery(
      ["transactions", criteria],
      async ({ pageParam = 0 }) => {
        return fetchTransactions(token(), {
          ...criteria,
          pageParam,
          fetchSize,
          terminalId: query.get("terminal")
        })
      },
      {
        getNextPageParam: (_lastGroup, groups) => groups.length,
      }
    );

  const flatData = useMemo(
    () => data?.pages?.flatMap(page => page) ?? [],
    [data])

  const totalFetched = flatData.length
  const totalDBRowCount = 1000

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 300 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  )

  const table = useReactTable({
    data: flatData,
    columns,
    state: {
    },
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    enableGlobalFilter: true
  })
  const { rows } = table.getRowModel();

  const titleWithTerminal = query.get("terminal");

  return (
    <>
      <Box p='6px 0px 22px 0px'>
        <Table variant='striped' color={textColor}>
          <Thead>
            <tr>
              <th align={"left"} style={{display: "flex", alignItems: 'center'}}>
                <Text fontSize='xl' color={textColor} fontWeight='bold' display="inline" mr="20px">
                  {`${title}${Boolean(titleWithTerminal) ? ' терминала №' + titleWithTerminal: ''}`}
                </Text>
                <SearchTransaction
                  onChange={e => setCriteria(prevCriteria => ({...prevCriteria, ...e}))}
                />
              </th>
            </tr>
          </Thead>
        </Table>
      </Box>

      <Box overflowX="auto"
           maxHeight="800px"
           onScroll={e => fetchMoreOnBottomReached(e.target)}
           ref={tableContainerRef}
      >
        <Table variant='striped' color={textColor}>
          <Thead>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}  my='.8rem' pl='0px' color='gray.400'>
                {headerGroup.headers.map((header, idx) => {
                  return (
                    <Th
                      key={header.id}
                      color='gray.400'
                      ps={idx === 0 ? "0px" : null}
                      pl={idx === 0 ? "20px" : null}
                    >
                      {header.column.columnDef.header}
                    </Th>
                  )
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={table.getHeaderGroups()[0].headers.length}>Загрузка...</Td>
              </Tr>
            ) : rows.map(row => {
              return (
                <TablesTransactionRow key={row.id} row={row} />
              )
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

export default TransactionsTable;
